import {
	UserDbFeatureFlags,
	UserDbFeatureFlagType,
	UserDto,
} from '@sweatpals/shared';
import { useMemo } from 'react';

/**
 * A hook that retrieves a user-level feature flag value.
 *
 * @param featureFlagName - The name of the feature flag to retrieve
 * @param currentUser - Optional user object that contains feature flags
 * @param defaultValue - Optional default value to return if the flag is not found
 * @returns The value of the specified feature flag for the user, default value if provided, or undefined
 *
 * @example
 * ```tsx
 * const isPremiumEnabled = useUserLevelFeatureFlag('isPremiumEnabled', currentUser);
 * 
 * // With default value
 * const isPremiumEnabled = useUserLevelFeatureFlag('isPremiumEnabled', currentUser, false);
 *
 * if (isPremiumEnabled === true) {
 *   // Show premium features
 * }
 * ```
 */
export const useUserLevelFeatureFlag = <T extends UserDbFeatureFlags>(
	featureFlagName: T, 
	currentUser?: UserDto | null, 
	defaultValue?: UserDbFeatureFlagType<T>
): UserDbFeatureFlagType<T> =>
	useMemo(() => {
		if (!currentUser) {
			return defaultValue;
		}

		if (!currentUser?.featureFlags) {
			console.warn('[UserFeatureFlag] No feature flags found for user: ', { currentUser });

			return defaultValue;
		}

		const flagsOfUser = currentUser?.featureFlags as unknown as UserDbFeatureFlags;

		// @ts-expect-error keys are always taken from the DTOs
		const currentVariant = flagsOfUser[featureFlagName] as unknown as UserDbFeatureFlagType<T>;
		console.info('[UserFeatureFlag] refreshed feature flag value: ', { featureFlagName, currentVariant });

		return currentVariant !== undefined ? currentVariant : defaultValue;
	}, [
		featureFlagName,
		currentUser?.id,
		defaultValue
	]);
